import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useFinishCourse } from "./useFinishCourse";
import { useConfettiWithSound } from "@/hooks/useConfettiWithSound";
import { useGetCourseCertificate } from "@/features/certificates/hooks/useGetCourseCertificate";
import certificateToast from "@/features/certificates/components/certificateToast/CertificateToast";

const CourseCompletionContext = createContext({
	setShowCertificateToast: () => {},
});

export function useCourseCompletionContext() {
	return useContext(CourseCompletionContext);
}

export function CourseCompletionProvider({ course, children }) {
	if (!course?.slug) return children;
	return (
		<CourseCompletionRunner course={course}>
			{children}
		</CourseCompletionRunner>
	);
}

function CourseCompletionRunner({ course, children }) {
	const { finishCourse } = useFinishCourse({ course });
	const { triggerConfetti, cleanup } = useConfettiWithSound();
	const { certificate } = useGetCourseCertificate(course.slug, {
		enabled: course?.is_completed === true,
	});

	const [showCertificateToast, setShowCertificateToast] = useState(false);
	const navigate = useNavigate();

	const coursePercentComplete = course?.total_units
		? Math.floor((100 * course.completed_units) / course.total_units)
		: 0;

	useEffect(() => {
		if (
			!showCertificateToast ||
			certificate?.certificate_id == null ||
			certificate?.is_unlocked == null ||
			certificate?.image_png == null
		)
			return;

		certificateToast(
			certificate.certificate_id,
			certificate.image_png,
			navigate
		);

		setShowCertificateToast(false);
	}, [
		showCertificateToast,
		certificate?.is_unlocked,
		certificate?.certificate_id,
		certificate?.image_png,
	]);

	useEffect(() => {
		return () => {
			cleanup();
		};
	}, [cleanup]);

	useEffect(() => {
		const handleAutoCompletion = async () => {
			if (coursePercentComplete === 100 && !course?.is_completed) {
				try {
					await Promise.all([finishCourse(), triggerConfetti()]);
					setShowCertificateToast(true);
				} catch (error) {
					console.error("Error during auto-completion:", error);
				}
			}
		};

		handleAutoCompletion();
	}, [coursePercentComplete, course?.is_completed]);

	return (
		<CourseCompletionContext.Provider value={{ setShowCertificateToast }}>
			{children}
		</CourseCompletionContext.Provider>
	);
}
